import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const VisitasRealizadas = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [visitas, setVisitas] = useState([]);
  const [filteredVisitas, setFilteredVisitas] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [selectedVisita, setSelectedVisita] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Cargar visitas al montar el componente
  useEffect(() => {
    if (user?.rol !== 'S') {
      setError('No tienes permisos para acceder a esta sección');
      setLoading(false);
      return;
    }

    fetchVisitas();
  }, [user]);

  // Filtrar visitas cuando cambian los filtros
  useEffect(() => {
    let filtered = [...visitas];

    // Filtro por término de búsqueda (busca en vigilante, comentario, puesto)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(visita => 
        (visita.Vigilante_de_turno && visita.Vigilante_de_turno.toLowerCase().includes(term)) ||
        (visita.comentario && visita.comentario.toLowerCase().includes(term)) ||
        (visita.punto_nombre && visita.punto_nombre.toLowerCase().includes(term))
      );
    }

    // Filtro por fecha desde
    if (fechaDesde) {
      filtered = filtered.filter(visita => {
        const visitaFecha = new Date(visita.fecha);
        const desdeFecha = new Date(fechaDesde);
        return visitaFecha >= desdeFecha;
      });
    }

    // Filtro por fecha hasta
    if (fechaHasta) {
      filtered = filtered.filter(visita => {
        const visitaFecha = new Date(visita.fecha);
        const hastaFecha = new Date(fechaHasta);
        hastaFecha.setHours(23, 59, 59, 999); // Incluir todo el día
        return visitaFecha <= hastaFecha;
      });
    }

    // Ordenar siempre por fecha descendente (más recientes primero)
    filtered.sort((a, b) => {
      const fechaA = new Date(a.fecha);
      const fechaB = new Date(b.fecha);
      return fechaB - fechaA; // Orden descendente
    });

    setFilteredVisitas(filtered);
  }, [visitas, searchTerm, fechaDesde, fechaHasta]);

  const fetchVisitas = async () => {
    try {
      setLoading(true);
      setError('');

      const url = new URL(getApiUrl('SUPERVISION', 'VISITAS_REALIZADAS'));
      url.searchParams.set('username', user?.username || '');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setVisitas(data.data || []);
        setFilteredVisitas(data.data || []);
      } else {
        setError(data.error || 'Error al cargar las visitas realizadas');
        setVisitas([]);
        setFilteredVisitas([]);
      }
    } catch (error) {
      console.error('Error cargando visitas:', error);
      setError('Error de conexión al cargar las visitas realizadas');
      setVisitas([]);
      setFilteredVisitas([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fechaString) => {
    if (!fechaString) return '-';
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return fechaString;
    }
  };

  const limpiarFiltros = () => {
    setSearchTerm('');
    setFechaDesde('');
    setFechaHasta('');
  };

  const handleShowDetails = (visita) => {
    setSelectedVisita(visita);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedVisita(null);
  };

  // Solo validar el rol, NO validar puesto asignado
  // Los supervisores (rol 'S') NO necesitan tener un puesto asignado
  // Solo consultan sus registros de supervision_registro
  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No hay usuario autenticado.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (user?.rol !== 'S') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No tienes permisos para acceder a esta sección. Solo los supervisores pueden acceder.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Visitas Realizadas</h1>
          <p className="mt-2 text-sm text-gray-600">Consulta y busca tus registros de asistencia</p>
        </div>

        {/* Filtros de búsqueda */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros de Búsqueda</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Búsqueda por texto */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Vigilante, comentario, puesto..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Fecha desde */}
            <div>
              <label htmlFor="fechaDesde" className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Desde
              </label>
              <input
                type="date"
                id="fechaDesde"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Fecha hasta */}
            <div>
              <label htmlFor="fechaHasta" className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Hasta
              </label>
              <input
                type="date"
                id="fechaHasta"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Botón limpiar filtros */}
          {(searchTerm || fechaDesde || fechaHasta) && (
            <div className="mt-4">
              <button
                onClick={limpiarFiltros}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          )}
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Tabla de resultados */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">Cargando visitas...</p>
          </div>
        ) : filteredVisitas.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-4 text-gray-600">
              {visitas.length === 0 
                ? 'No se encontraron visitas realizadas' 
                : 'No se encontraron resultados con los filtros aplicados'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-xs text-gray-600">
                  Mostrando <span className="font-semibold">{filteredVisitas.length}</span> de <span className="font-semibold">{visitas.length}</span> visitas
                </p>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punto
                      </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                      </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registro
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVisitas.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-2 py-3 text-center text-xs text-gray-500">
                        No se encontraron visitas con los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    filteredVisitas.map((visita) => {
                      // Determinar el color del botón según el estado
                      const tieneGPS = visita.gps_latitud && visita.gps_longitud;
                      const esFalto = visita.es_Falto === 'S';
                      const esVacante = visita.es_Vacante === 'S';
                      
                      // Obtener hora de la fecha
                      const fechaObj = new Date(visita.fecha);
                      const horaFormateada = fechaObj.toLocaleTimeString('es-ES', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit'
                      });
                      const fechaFormateada = fechaObj.toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                      
                      // Determinar color del botón (prioridad: Falto > Vacante > GPS > Normal)
                      let buttonClass = 'bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 border border-indigo-200 hover:border-indigo-300';
                      let textColor = 'text-indigo-900 group-hover:text-indigo-700';
                      let iconColor = 'text-indigo-400 group-hover:text-indigo-600';
                      let badgeText = '';
                      let badgeColor = '';
                      
                      if (esFalto) {
                        buttonClass = 'bg-gradient-to-r from-red-50 to-rose-50 hover:from-red-100 hover:to-rose-100 border border-red-200 hover:border-red-300';
                        textColor = 'text-red-900 group-hover:text-red-700';
                        iconColor = 'text-red-400 group-hover:text-red-600';
                        badgeText = 'FALTO';
                        badgeColor = 'text-red-600';
                      } else if (esVacante) {
                        buttonClass = 'bg-gradient-to-r from-yellow-50 to-amber-50 hover:from-yellow-100 hover:to-amber-100 border border-yellow-200 hover:border-yellow-300';
                        textColor = 'text-yellow-900 group-hover:text-yellow-700';
                        iconColor = 'text-yellow-400 group-hover:text-yellow-600';
                        badgeText = 'VACANTE';
                        badgeColor = 'text-yellow-600';
                      } else if (tieneGPS) {
                        buttonClass = 'bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 border border-blue-200 hover:border-blue-300';
                        textColor = 'text-blue-900 group-hover:text-blue-700';
                        iconColor = 'text-blue-400 group-hover:text-blue-600';
                        // No mostrar badge para GPS, solo el color azul indica que tiene GPS
                      }
                      
                      // Obtener punto formateado
                      const puntoFormateado = visita.id_punto 
                        ? `${environment.puntoChar || ''}${visita.id_punto}`
                        : visita.punto_id_generado 
                          ? `${environment.puntoChar || ''}${visita.punto_id_generado}`
                          : '-';
                      
                      return (
                      <tr key={visita.id} className="hover:bg-gray-50">
                          <td className="px-2 py-2 text-xs font-medium text-gray-900 align-top">
                            {puntoFormateado}
                        </td>
                          <td className="px-2 py-2 text-xs text-gray-900 align-top break-words">
                            {visita.punto_nombre || visita.nombre_punto || '-'}
                        </td>
                          <td className="px-2 py-2 align-top">
                            <button
                              onClick={() => handleShowDetails(visita)}
                              className={`w-full px-2 py-2 rounded-md text-left transition-all duration-200 shadow-sm hover:shadow-md group ${buttonClass}`}
                            >
                              <div className="flex flex-col">
                                <div className="flex items-center justify-between gap-1">
                                  <span className={`text-xs font-semibold leading-tight ${textColor}`}>
                                    {horaFormateada}
                                  </span>
                                  <svg className={`w-3 h-3 transition-colors flex-shrink-0 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                                <div className="flex items-center justify-between mt-0.5">
                                  <span className={`text-xs font-medium leading-tight ${textColor}`}>
                                    {fechaFormateada}
                                  </span>
                                  {badgeText && (
                                    <span className={`text-xs font-medium leading-tight ${badgeColor}`}>
                                      {badgeText}
                          </span>
                                  )}
                                </div>
                              </div>
                            </button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                  </tbody>
                </table>
              </div>
            </div>
        )}

            {/* Modal de detalles */}
            {showDetailsModal && selectedVisita && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
                onClick={handleCloseDetails}
              >
                <div 
                  className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header del modal */}
                  <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">Detalles de la Visita</h3>
                    <button
                      onClick={handleCloseDetails}
                      className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      aria-label="Cerrar"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Contenido del modal */}
                  <div className="px-6 py-4 space-y-4">
                    {/* Fecha y Hora */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Fecha y Hora</p>
                      <p className="text-sm font-semibold text-gray-900">{formatFecha(selectedVisita.fecha)}</p>
                    </div>

                    {/* Puesto */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Puesto</p>
                      <p className="text-sm text-gray-900">{selectedVisita.punto_nombre || `Puesto ${selectedVisita.punto_id_generado}`}</p>
                    </div>

                    {/* Vigilante */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Vigilante de Turno</p>
                      <p className="text-sm text-gray-900">{selectedVisita.Vigilante_de_turno || '-'}</p>
                    </div>

                    {/* Tipo Turno */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Tipo de Turno</p>
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        selectedVisita.es_Falto === 'S' 
                          ? 'bg-red-100 text-red-800'
                          : selectedVisita.es_Vacante === 'S'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {selectedVisita.es_Falto === 'S' 
                          ? 'Doblando por Falto'
                          : selectedVisita.es_Vacante === 'S'
                          ? 'Doblando por Vacante'
                          : 'Normal'}
                      </span>
                    </div>

                    {/* Doblando */}
                    {selectedVisita.Doblando && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Doblando</p>
                        <p className="text-sm text-gray-900">{selectedVisita.Doblando === 'SI' ? 'Sí' : 'No'}</p>
                      </div>
                    )}

                    {/* Comentario */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Comentario</p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedVisita.comentario || '-'}</p>
                    </div>

                    {/* GPS */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Coordenadas GPS</p>
                      {selectedVisita.gps_latitud && selectedVisita.gps_longitud ? (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Latitud:</span> {selectedVisita.gps_latitud}
                          </p>
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Longitud:</span> {selectedVisita.gps_longitud}
                          </p>
                          <span className="inline-flex items-center text-xs text-green-600">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            GPS registrado
                          </span>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No disponible</p>
                      )}
                    </div>

                    {/* Reportar Novedad */}
                    {selectedVisita.Reportar_Novedad_Cliente && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Reportar Novedad a Cliente</p>
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          selectedVisita.Reportar_Novedad_Cliente === 'S' 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedVisita.Reportar_Novedad_Cliente === 'S' ? 'Sí' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer del modal */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <button
                      onClick={handleCloseDetails}
                      className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            )}

      </div>
    </Layout>
  );
};

export default VisitasRealizadas;

